import React, { useState, useEffect, useCallback } from 'react';
import {
  loadDefinitions,
  saveDefinition,
  createDefinition,
  deleteDefinition,
  CATEGORIES,
} from '../store/assetDefinitions';
import AssetDefinitionEditor from './AssetDefinitionEditor';
import ShapePreview from './ShapePreview';

const AssetDefinitionsView = () => {
  const [definitions, setDefinitions] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const defs = await loadDefinitions();
      setDefinitions(defs);
    } catch (err) {
      console.error('Failed to load definitions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleSave = async (updatedDef) => {
    await saveDefinition(updatedDef);
    await refresh();
  };

  const handleCreate = async () => {
    const id = 'asset_' + Date.now();
    const newDef = {
      id,
      category: 'generation',
      name: 'New Asset',
      icon: '⚡',
      shape: { width: 140, height: 90 },
      frameColor: '#4ecca3',
      connectorColor: '#4ecca3',
      connectors: [],
      maxSelectableConnectors: 2,
      fields: [
        { id: 'f1', name: 'capacity', label: 'Capacity', type: 'text', defaultValue: '', required: true },
        { id: 'f2', name: 'status', label: 'Status', type: 'select', options: ['active', 'inactive', 'planned'], defaultValue: 'active', required: true },
      ],
    };
    await createDefinition(newDef);
    await refresh();
    setEditingId(id);
  };

  const handleDelete = async (id) => {
    if (window.confirm(`Delete asset definition "${id}"? This removes the database schema.`)) {
      await deleteDefinition(id);
      if (editingId === id) setEditingId(null);
      await refresh();
    }
  };

  const handleDuplicate = async (def) => {
    const newId = def.id + '_copy_' + Date.now();
    const copy = {
      ...JSON.parse(JSON.stringify(def)),
      id: newId,
      name: def.name + ' (Copy)',
    };
    await createDefinition(copy);
    await refresh();
    setEditingId(newId);
  };

  const editingDef = definitions.find((d) => d.id === editingId);

  const filteredDefs = filterCategory === 'all'
    ? definitions
    : definitions.filter((d) => d.category === filterCategory);

  // Group by category
  const grouped = {};
  filteredDefs.forEach((d) => {
    if (!grouped[d.category]) grouped[d.category] = [];
    grouped[d.category].push(d);
  });

  if (editingDef) {
    return (
      <AssetDefinitionEditor
        definition={editingDef}
        onSave={handleSave}
        onCancel={() => setEditingId(null)}
      />
    );
  }

  if (loading) {
    return (
      <div className="def-list-view">
        <div className="def-list-header">
          <div>
            <h1>Asset Definitions</h1>
            <p className="def-list-subtitle">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="def-list-view">
      {/* Header */}
      <div className="def-list-header">
        <div>
          <h1>Asset Definitions</h1>
          <p className="def-list-subtitle">
            Configure database schemas, shapes, and connectors for each asset type.
          </p>
        </div>
        <button className="btn-accent" onClick={handleCreate}>+ New Asset Type</button>
      </div>

      {/* Category filter */}
      <div className="def-category-filter">
        <button
          className={`filter-btn ${filterCategory === 'all' ? 'active' : ''}`}
          onClick={() => setFilterCategory('all')}
        >
          All ({definitions.length})
        </button>
        {CATEGORIES.map((cat) => {
          const count = definitions.filter((d) => d.category === cat.id).length;
          return (
            <button
              key={cat.id}
              className={`filter-btn ${filterCategory === cat.id ? 'active' : ''}`}
              onClick={() => setFilterCategory(cat.id)}
              style={filterCategory === cat.id ? { borderColor: cat.color, color: cat.color } : {}}
            >
              <span className="filter-dot" style={{ background: cat.color }} />
              {cat.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Asset cards */}
      <div className="def-cards">
        {Object.entries(grouped).map(([catId, defs]) => {
          const cat = CATEGORIES.find((c) => c.id === catId);
          return (
            <div key={catId} className="def-card-group">
              <h3 className="def-card-group-title" style={{ color: cat?.color }}>
                {cat?.label || catId}
              </h3>
              <div className="def-card-grid" style={{
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: 10,
              }}>
                {defs.map((def) => (
                  <div key={def.id} className="def-card" style={{
                    borderColor: def.frameColor,
                    padding: 10,
                  }}>
                    <div className="def-card-preview" style={{
                      height: 90,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                    }}>
                      <ShapePreview
                        width={100}
                        height={100}
                        frameColor={def.frameColor}
                        connectorColor={def.connectorColor}
                        connectors={def.connectors}
                        icon={def.icon}
                        name={def.name}
                        scale={0.55}
                      />
                    </div>
                    <div className="def-card-info" style={{ padding: '6px 0 4px 0' }}>
                      <div className="def-card-name" style={{ fontSize: 13, gap: 4 }}>
                        <span className="def-card-icon" style={{ fontSize: 14 }}>{def.icon}</span>
                        {def.name}
                      </div>
                      <div className="def-card-meta" style={{ fontSize: 10, gap: 6 }}>
                        <span>{def.fields.length} fields</span>
                        <span>{def.connectors.filter((c) => c.enabled).length} connectors</span>
                        <span>{def.connectors.filter((c) => c.favorite).length} favorites</span>
                      </div>
                    </div>
                    <div className="def-card-actions" style={{ gap: 4, paddingTop: 4 }}>
                      <button className="btn-card" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => setEditingId(def.id)}>Edit</button>
                      <button className="btn-card" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => handleDuplicate(def)}>Dup</button>
                      <button className="btn-card danger" style={{ fontSize: 10, padding: '3px 8px' }} onClick={() => handleDelete(def.id)}>Del</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {filteredDefs.length === 0 && (
          <div className="def-empty-state">
            No asset definitions found. Create one to get started.
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetDefinitionsView;
